import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { getLanguageCode } from 'js/lib/language';
import Retracked from 'js/lib/retracked';
import thirdParty from 'js/lib/thirdParty';

declare global {
  namespace ReCaptchaV2 {
    interface ReCaptcha {
      enterprise: ReCaptcha;
    }
  }

  interface Window {
    recaptchaOnLoadCallback?: () => void;
  }
}

export class RecaptchaClient {
  private declare _recaptchaWidgetId: number;

  constructor(recaptchaWidgetId: number) {
    this._recaptchaWidgetId = recaptchaWidgetId;
  }

  private _grecaptcha() {
    return window.grecaptcha.enterprise;
  }

  /**
   * Explicitly execute reCAPTCHA. Required for invisible reCAPTCHA.
   */
  execute() {
    this._grecaptcha().execute(this._recaptchaWidgetId);
  }

  /**
   * Reset reCAPTCHA.
   */
  reset() {
    this._grecaptcha().reset(this._recaptchaWidgetId);
  }

  /**
   * Get reCAPTCHA token. Auto retry if token is invalid.
   */
  getToken(): string | undefined {
    const token = this._grecaptcha().getResponse(this._recaptchaWidgetId);

    if (token === '' || !token) {
      this.execute(); // Invalid token, retry
      return undefined;
    }

    return token;
  }
}

export enum RecaptchaError {
  EXPIRED_RECAPTCHA_RESPONSE = 'EXPIRED_RECAPTCHA_RESPONSE',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

const RECAPTCHA_RENDER_CONTAINER_ID = 'recaptcha';

export type PropsFromCaller = {
  /**
   * Generated from Google Cloud console. A new one should be generated for each team.
   */
  siteKey: string;

  /**
   * Called when user successfully solves the reCAPTCHA.
   */
  onSuccess: (token: string) => void;

  /**
   * Called when reCAPTCHA is ready. Use either the onSuccess callback or the client to get the token.
   */
  onReady: (recaptchaClient: RecaptchaClient) => void;

  /**
   * Called when reCAPTCHA fails.
   */
  onError?: (error: RecaptchaError) => void;

  /**
   * Use of invisible reCAPTCHA is discouraged by Google. Make sure your site key is configured for invisible reCAPTCHA.
   */
  isInvisible?: boolean;
};

class RecaptchaEnterprise extends React.Component<PropsFromCaller> {
  static contextTypes = {
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof RecaptchaEnterprise.contextTypes>;

  async componentDidMount() {
    // window is guaranteed to exist
    window.recaptchaOnLoadCallback = this.recaptchaOnLoadCallback;
    await this.loadScript();
  }

  getLanguage() {
    const code = getLanguageCode();
    // This is reCAPTCHA specific.
    return code === 'zh-mo' ? 'zh' : code;
  }

  async loadScript() {
    // window contains grecaptcha after component remounts. Need to manually trigger the callback.
    if (window.grecaptcha) {
      this.recaptchaOnLoadCallback();
      return;
    }

    try {
      return await thirdParty.loadScript({
        url: `https://www.google.com/recaptcha/enterprise.js?onload=recaptchaOnLoadCallback&render=explicit&hl=${this.getLanguage()}`,
      });
    } catch {
      return await thirdParty.loadScript({
        url: `https://www.recaptcha.net/recaptcha/enterprise.js?onload=recaptchaOnLoadCallback&render=explicit&hl=${this.getLanguage()}`,
      });
    }
  }

  trackExecution() {
    const { _eventData } = this.context;
    Retracked.trackComponent(_eventData, {}, 'recaptcha_executed', 'show');
  }

  onErrorHandler(error: RecaptchaError) {
    const { onError } = this.props;
    if (onError) {
      onError(error);
    }
    this.trackExecution();
  }

  onSuccessHandler = (token: string) => {
    const { onSuccess } = this.props;
    onSuccess(token);
    this.trackExecution();
  };

  recaptchaOnLoadCallback = () => {
    const { siteKey, isInvisible, onReady } = this.props;
    const grecaptcha = window.grecaptcha.enterprise;

    const recaptchaWidgetId = grecaptcha.render(RECAPTCHA_RENDER_CONTAINER_ID, {
      callback: this.onSuccessHandler,
      'error-callback': () => this.onErrorHandler(RecaptchaError.EXPIRED_RECAPTCHA_RESPONSE),
      'expired-callback': () => this.onErrorHandler(RecaptchaError.UNKNOWN_ERROR),
      size: isInvisible ? 'invisible' : undefined,
      sitekey: siteKey,
    });

    const recaptchaClient = new RecaptchaClient(recaptchaWidgetId);
    onReady(recaptchaClient);
  };

  render() {
    return <div id={RECAPTCHA_RENDER_CONTAINER_ID} />;
  }
}

export default Retracked.createTrackedContainer<PropsFromCaller>(() => ({}))(RecaptchaEnterprise);
